// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';

export interface CloverOAuthLaunchContext {
    readonly merchantId: string;
    readonly clientId: string;
}

export const CloverOAuthLaunchContextSchema: z.ZodType<CloverOAuthLaunchContext> = z
    .object({
        merchantId: z.string().min(1),
        clientId: z.string().min(1),
    })
    .strict();

function getNonEmptySearchParameter(
    searchParams: URLSearchParams,
    key: string
): string | undefined {
    const value = searchParams.get(key)?.trim();
    return value === undefined || value.length === 0 ? undefined : value;
}

export function parseCloverOAuthLaunchContext(
    searchParams: URLSearchParams
): CloverOAuthLaunchContext | null {
    const merchantId = getNonEmptySearchParameter(searchParams, 'merchant_id');
    const clientId = getNonEmptySearchParameter(searchParams, 'client_id');
    if (merchantId === undefined || clientId === undefined) {
        return null;
    }
    return CloverOAuthLaunchContextSchema.safeParse({ merchantId, clientId }).data ?? null;
}

export function buildCloverOAuthLaunchPath(context: CloverOAuthLaunchContext): string {
    const searchParams = new URLSearchParams({
        merchant_id: context.merchantId,
        client_id: context.clientId,
    });
    return `/clover/launch?${searchParams.toString()}`;
}

export function buildCloverOAuthLoginPath(context: CloverOAuthLaunchContext): string {
    const searchParams = new URLSearchParams({
        clover_merchant_id: context.merchantId,
        clover_client_id: context.clientId,
    });
    return `/login?${searchParams.toString()}`;
}

export function parseCloverOAuthLoginContext(
    searchParams: URLSearchParams
): CloverOAuthLaunchContext | null {
    const merchantId = getNonEmptySearchParameter(searchParams, 'clover_merchant_id');
    const clientId = getNonEmptySearchParameter(searchParams, 'clover_client_id');
    if (merchantId === undefined || clientId === undefined) {
        return null;
    }
    return CloverOAuthLaunchContextSchema.safeParse({ merchantId, clientId }).data ?? null;
}

export function appendCloverOAuthLoginContext(
    searchParams: URLSearchParams,
    context: CloverOAuthLaunchContext | null
): void {
    if (context === null) {
        return;
    }
    searchParams.set('clover_merchant_id', context.merchantId);
    searchParams.set('clover_client_id', context.clientId);
}
